import { useState } from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Icon,
  Item,
  Text,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { A3InnuvaMapping } from 'modules/payroll_integrations_a3innuva/lib/types'

import { MappingTable } from '../../CodeFillers/MappingTable'
import { upsertJobPositionMappings } from '../../CodeFillers/helpers'

interface LegalEntity {
  id: number
  legalName: string
}

interface Level {
  id: string
  roleName: string
  name: string
}
interface FactorialRolesSectionProps {
  uniqueLegalEntities: LegalEntity[]
  groupedLevelsByLegalEntity: Array<{
    entityId: number
    levels: Level[]
  }>
  jobPositions?: {
    mappings?: A3InnuvaMapping[]
  }
  options: (legalEntityId: number) => Item[]
  updateMappings: (newMappings: A3InnuvaMapping[]) => void
  loading: boolean
}

export const FactorialRolesSection = ({
  uniqueLegalEntities,
  groupedLevelsByLegalEntity,
  jobPositions,
  options,
  updateMappings,
  loading,
}: FactorialRolesSectionProps) => {
  const cleanedJobPositions = groupedLevelsByLegalEntity.map((group) => {
    // Group levels by roleName to check which roles have non-default levels
    const levelsByRole = group.levels.reduce<{ [key: string]: Level[] }>((acc, level) => {
      const roleName = level.roleName
      acc[roleName] = [...(acc[roleName] || []), level]
      return acc
    }, {})

    // Filter levels: keep default only if it's the only level for that role
    const filteredLevels = group.levels.filter((level) => {
      const roleLevels = levelsByRole[level.roleName] ?? []
      if (level.name === '__default__') {
        // Keep default only if it's the only level for this role
        return roleLevels.length === 1
      }
      return true
    })

    return {
      ...group,
      levels: filteredLevels,
    }
  })

  const defaultCardsState = () => {
    const defaultStates: Record<string, boolean> = {}

    uniqueLegalEntities.forEach((legalEntity) => {
      cleanedJobPositions.forEach((groupedLevels) => {
        defaultStates[legalEntity.id] = groupedLevels.levels.length == 0
      })
    })
    return defaultStates
  }

  const [openStates, setOpenStates] = useState<Record<string, boolean>>(defaultCardsState())

  const toggleCard = (id: string) => {
    setOpenStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const setFormValues = (
    selectedItem: Item,
    level: { id: string; roleName: string },
    legalEntityId: number
  ) => {
    const newMappings = upsertJobPositionMappings(
      jobPositions?.mappings || [],
      selectedItem,
      level.id.toString(),
      legalEntityId.toString()
    )
    updateMappings(newMappings)
  }

  const findSelection = (levelId: string, legalEntityId: number) => {
    const currentMapping = (jobPositions?.mappings || []).find((mapping) => {
      return (
        mapping.factorial_id.toString() === levelId.toString() &&
        mapping?.meta?.legal_entity_id === legalEntityId.toString()
      )
    })
    if (!currentMapping) return ''

    const selectedOption = options(legalEntityId).find(
      (option) => option.value === currentMapping.a3innuva_id
    )
    return selectedOption?.label || ''
  }

  return (
    <>
      {uniqueLegalEntities.map((legalEntity) => {
        const legalEntityLevels =
          cleanedJobPositions.find((group) => group.entityId === legalEntity.id)?.levels || []

        if (legalEntityLevels.length === 0) return null
        return (
          <Box gap="s32" key={legalEntity.id}>
            <Box background="grey200" borderRadius={{ all: 'abs008' }}>
              <CollapsableCard
                open={openStates[legalEntity.id] ?? false}
                contentBackground="grey000White"
                contentPadding="s0"
                Card={
                  <Box flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="row" gap="s8" alignItems="center">
                      <Icon.Large icon="building" />
                      <Text.Header3>{legalEntity.legalName}</Text.Header3>
                    </Box>
                    <Button
                      iconName={openStates[legalEntity.id] ? 'chevron-up' : 'chevron-down'}
                      onClick={() => toggleCard(legalEntity.id.toString())}
                      type="secondary"
                    />
                  </Box>
                }
                Content={
                  <MappingTable
                    title={i18n.t(
                      'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.mapping_table.a3innuva_fields'
                    )}
                    items={legalEntityLevels}
                    options={options(legalEntity.id)}
                    getItemId={(item) => item.id}
                    getItemLabel={(item) => `${item.roleName} - ${item.name}`}
                    onSelect={(selectedItem, item) =>
                      setFormValues(selectedItem, item, legalEntity.id)
                    }
                    findSelection={(item) => {
                      return findSelection(item, legalEntity.id)
                    }}
                    selectPlaceholder={i18n.t(
                      'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.levels.default_value_placeholder'
                    )}
                    loading={loading}
                    customBorder={{
                      left: { color: 'grey000White', width: 's1', style: 'solid' },
                      right: { color: 'grey000White', width: 's1', style: 'solid' },
                      bottom: { color: 'grey000White', width: 's1', style: 'solid' },
                    }}
                  />
                }
              />
            </Box>
          </Box>
        )
      })}
    </>
  )
}
